import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight, LifeBuoy, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';

/**
 * Phase 07 — one concept, one honest verdict.
 * 80% is the gate. Below it every item is explained and a FRESH set is offered;
 * after the second miss the app stops asking the child to try harder and
 * rebuilds the idea instead.
 */
export function MasteryQuizDialog({ open, onOpenChange, concept, onFinished }) {
  const [phase, setPhase] = useState('intro');   // intro | quiz | result | remediation
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPhase('intro'); setAttempt(null); setAnswers({}); setResult(null); setPlan(null); };

  const start = async () => {
    setBusy(true);
    try {
      const res = await apiPost('/premium/v1/mastery/attempts', {
        concept_id: concept.concept_id, subject: concept.subject, chapter: concept.chapter,
      });
      setAttempt(res); setAnswers({}); setResult(null); setPhase('quiz');
    } catch (e) {
      const msg = e?.message || 'Could not start that quiz just now';
      if (/fix the shaky bit/i.test(msg)) { await openRemediation(); } else { toast.error(msg); }
    }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await apiPost(`/premium/v1/mastery/attempts/${attempt.attempt_id}/submit`, { answers });
      setResult(res); setPhase('result'); onFinished?.(res);
    } catch (e) { toast.error(e?.message || 'Could not submit that attempt'); }
    setBusy(false);
  };

  const openRemediation = async () => {
    setBusy(true);
    try {
      const res = await apiGet(`/premium/v1/remediation/${concept.concept_id}?subject=${encodeURIComponent(concept.subject)}&chapter=${encodeURIComponent(concept.chapter)}`);
      setPlan(res); setPhase('remediation');
    } catch (e) { toast.error(e?.message || 'Could not open the remediation plan'); }
    setBusy(false);
  };

  const finishRemediation = async () => {
    setBusy(true);
    try {
      await apiPost(`/premium/v1/remediation/${concept.concept_id}/complete`, {});
      toast.success('Final attempt unlocked — one clean go.');
      await start();
    } catch (e) { toast.error(e?.message || 'Could not unlock the final attempt'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="mastery-quiz-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{concept?.title}</DialogTitle>
          <DialogDescription className="font-body text-xs">
            {concept?.subject} · {concept?.chapter} — 80% is the gate, and it says something about the questions, not about you.
          </DialogDescription>
        </DialogHeader>

        {phase === 'intro' && (
          <div className="space-y-3" data-testid="mastery-intro">
            <p className="font-body text-sm text-slate-600">
              Five questions on this one concept. Miss the gate and I'll explain every single item, then hand you a
              fresh set — never the same questions twice in a row.
            </p>
            <Button onClick={start} disabled={busy} data-testid="mastery-start-btn" className="rounded-xl">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Start the concept quiz
            </Button>
          </div>
        )}

        {phase === 'quiz' && attempt && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="mastery-questions">
            <div className="flex items-center justify-between text-[11px] font-heading uppercase tracking-widest text-slate-400">
              <span>Attempt {attempt.attempt_no} · {attempt.phase}</span>
              <span data-testid="mastery-attempts-left">{attempt.attempts_left_today} left today</span>
            </div>
            {attempt.content_debt && (
              <p className="text-[11px] font-body text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-2" data-testid="mastery-content-debt">
                This chapter's question pool is still thin — we've flagged it internally so it gets deepened.
              </p>
            )}
            {attempt.questions.map((q, i) => (
              <div key={q.ref} className="rounded-2xl border border-slate-200 p-3" data-testid={`mastery-q-${i}`}>
                <p className="font-body text-sm text-slate-800">{i + 1}. {q.question}</p>
                <div className="mt-2 grid gap-1.5">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers(a => ({ ...a, [i]: oi }))}
                      data-testid={`mastery-q-${i}-opt-${oi}`}
                      className={`text-left text-[13px] font-body px-3 py-2 rounded-xl border transition-colors ${answers[i] === oi ? 'border-indigo-400 bg-indigo-50 text-indigo-900' : 'border-slate-200 hover:border-slate-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={submit} disabled={busy || Object.keys(answers).length < attempt.questions.length}
              data-testid="mastery-submit-btn" className="rounded-xl w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit and see where I stand
            </Button>
          </div>
        )}

        {phase === 'result' && result && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="mastery-result">
            <div className={`rounded-2xl p-3 border ${result.passed ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="font-heading text-base text-slate-900" data-testid="mastery-score">
                {result.score_pct}% · {result.correct}/{result.total}
              </p>
              <p className="font-body text-sm text-slate-700 mt-1" data-testid="mastery-message">{result.message}</p>
              <p className="font-body text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Mastery {result.mastery}% · back on {result.due_on}
                {result.exam_compressed ? ' (pulled closer because the exam is near)' : ''}
              </p>
            </div>

            {result.review.map((r, i) => (
              <div key={r.ref} className="rounded-2xl border border-slate-200 p-3" data-testid={`mastery-review-${i}`}>
                <p className="font-body text-sm text-slate-800 flex items-start gap-2">
                  {r.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    : <XCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />}
                  <span>{r.question}</span>
                </p>
                <p className="font-body text-[12px] text-slate-500 mt-1 pl-6">
                  Right answer: <span className="text-slate-700">{r.options[r.correct_index]}</span>
                </p>
                {r.explanation && <p className="font-body text-[12px] text-slate-600 mt-1 pl-6">{r.explanation}</p>}
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              {result.next_action === 'retry' && (
                <Button onClick={start} disabled={busy} data-testid="mastery-retry-btn" className="rounded-xl">
                  Fresh questions, one more go <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
              {result.next_action === 'remediation' && (
                <Button onClick={openRemediation} disabled={busy} data-testid="mastery-remediation-btn" className="rounded-xl">
                  <LifeBuoy className="w-4 h-4 mr-1.5" /> Rebuild this properly
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mastery-close-btn" className="rounded-xl">
                Close
              </Button>
            </div>
          </div>
        )}

        {phase === 'remediation' && plan && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="mastery-remediation">
            <p className="font-body text-sm text-slate-600">
              Two misses means the explanation needs to change — not that you need to try harder. Here's the order that works.
            </p>
            {(plan.prerequisites || []).filter(p => p.needs_repair).map(p => (
              <div key={p.concept_id} className="rounded-xl border border-rose-200 bg-rose-50 p-2.5" data-testid={`remediation-prereq-${p.concept_id}`}>
                <p className="font-heading text-[13px] text-rose-900">Repair first: {p.title} · {p.mastery}%</p>
                <p className="font-body text-[11px] text-rose-700">{p.reason}</p>
              </div>
            ))}
            <ol className="space-y-1.5">
              {plan.steps.map((s, i) => (
                <li key={s.key + i} className="rounded-xl border border-slate-200 p-2.5" data-testid={`remediation-step-${s.key}`}>
                  <p className="font-heading text-[13px] text-slate-800">{i + 1}. {s.title}</p>
                  <p className="font-body text-[11px] text-slate-500">{s.hint}</p>
                </li>
              ))}
            </ol>
            <Button onClick={finishRemediation} disabled={busy} data-testid="remediation-done-btn" className="rounded-xl w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} I've been through this — unlock my final attempt
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MasteryQuizDialog;
